// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Modals from "@insite/content-library/Components/Modals";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import WishListSharingStatus, { WishListSharingStatusStyles } from "@store/Overrides/Components/WishListSharingStatus";

import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
} from "@insite/mobius/index";

import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowiOSUpward from "@store/Library/Components/Toolkit/Icons/ArrowiOSUpward";
import CheckboxIndeterminate from "@store/Library/Components/Toolkit/Icons/Checkbox/CheckboxIndeterminate";
import Checkbox from "@store/MobiusOverrides/Checkbox/Checkbox";
import { IconProps } from "@store/MobiusOverrides/Icon/Icon";
import LoadingOverlay from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import useAreProductsLoading from "@store/Framework/Common/Hooks/useAreProductsLoading";
import { useVirtualizer } from "@store/Framework/Common/Utilities/TanstackVirtual/useVirtualizer";
import BuyingListDetailsLink from "@store/Library/Components/BuyingList/BuyingListDetailsLink";
import useBulkLists, { WishListModelWithHasProduct } from "@store/Library/Widgets/BulkLists/useBulkLists";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

import Icon from "@store/MobiusOverrides/Icon/Icon";
import React, { useCallback, useMemo, useState } from "react";
import { css } from "styled-components";

const customColGroup = (
    <colgroup>
        <col style={{ width: "5%" }} />
        <col style={{ width: "27.00%" }} />
        <col style={{ width: "27.00%" }} />
        <col style={{ width: "8%" }} />
        <col style={{ width: "21%" }} />
        <col style={{ width: "12%" }} />
    </colgroup>
);

type HeaderLabels = "Name" | "Description" | "Count" | "Status" | "Has Product";

const headerToPropertyMap: Record<HeaderLabels, (wishListModel: WishListModelWithHasProduct) => string | number> = {
    Name: wishList => wishList.name,
    Description: wishList => wishList.description,
    Count: wishList => Number(wishList.properties.wishListLineCount),
    Status: wishList => translate(getStatusText(wishList)),
    "Has Product": wishList => (wishList.hasProduct ? "Yes" : "No"),
};

export const getStatusText = (wishList: WishListModel) => {
    if (wishList.isSharedList) {
        return `Shared by ${wishList.sharedByDisplayName}`;
    }
    if (wishList.wishListSharesCount === 1) {
        return "Shared with 1 other";
    }
    if (wishList.wishListSharesCount > 1) {
        return `Shared with ${wishList.wishListSharesCount} others`;
    }
    return "Private";
};
enum fields {
    listType = "listType",
}
const sortLists = (lists: WishListModelWithHasProduct[] | undefined, sortedBy?: string) => {
    if (!lists) {
        return [];
    }
    if (!sortedBy) {
        return lists;
    }
    const isOrderDescending = sortedBy.includes("desc");
    const header = sortedBy.replace(" desc", "") as HeaderLabels;
    const sortedLists = [...lists].sort((a, b) => {
        const sortPropertyA = headerToPropertyMap[header](a);
        const sortPropertyB = headerToPropertyMap[header](b);
        if (isOrderDescending) {
            return sortPropertyA > sortPropertyB ? -1 : sortPropertyA < sortPropertyB ? 1 : 0;
        }
        return sortPropertyA > sortPropertyB ? 1 : sortPropertyA < sortPropertyB ? -1 : 0;
    });
    return sortedLists;
};
interface BulkListDataTableStyles {
    header?: InjectableCss;
    cell?: DataTableCellProps;
    dataTable?: InjectableCss;
    statusExtendedStyles?: WishListSharingStatusStyles;
    iconProps?: IconProps;
    scrollToTopArrow?: InjectableCss;
    parentContainer?: InjectableCss;
}
const dataTableStyles: BulkListDataTableStyles = {
    parentContainer: {
        css: css`
            height: 60vh;
            overflow-y: auto;
            padding-inline: 0.5rem;
        `,
    },
    scrollToTopArrow: {
        css: css`
            position: absolute;
            display: flex;
            z-index: 50;

            bottom: 3%;
            right: 3%;
            opacity: 0;
            transition: opacity 0.4s ease-in-out 0.2s;
            justify-content: flex-end;
            cursor: pointer;
            svg {
                color: #505d67;
                background-color: transparent;
                transition: all 0.5s ease-in-out;
                border-radius: 0.8rem;
                outline: 2px solid #505d67;
            }
            :hover {
                svg {
                    transform: scale(1.08);
                    border-radius: 50%;
                    color: ${getColor("primary")};
                }
            }
        `,
    },
    iconProps: {
        size: 28,

        css: css`
            opacity: 0.65;
            width: 100%;
            color: ${getColor("primary")};
            display: flex;
            justify-content: center;
            svg {
                stroke-width: 3;
                background-color: ${getColor("common.checkboxPickerBackground")};
                border-radius: 50%;
            }
        `,
    },
    statusExtendedStyles: {
        statusText: {
            ellipsis: true,
            css: css`
                display: unset;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                width: 100%;
            `,
        },
    },
    dataTable: {
        css: css`
            table-layout: fixed;
            border-collapse: separate;
            border-spacing: 0 0.125rem;
            position: relative;
        `,
    },

    header: {
        css: css`
            background-color: ${getColor("common.checkboxPickerBackground")};
            z-index: 50;
            > button {
                padding-top: 0.25rem;
            }
        `,
    },
    cell: {
        css: css`
            width: 100%;
            overflow: hidden;
            a {
                width: 100%;
            }
            span {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                width: 100%;
                display: block;
                font-size: ${pxToRem(14)};
            }
        `,
        typographyProps: {
            css: css`
                padding-top: ${pxToRem(6)};
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                width: 100%;
                display: block;
                font-size: ${pxToRem(14)};
            `,
        },
    },
};
const iconDictionary = {
    hasProduct: Check,
    doesNotHaveProduct: X,
    indeterminate: CheckboxIndeterminate,
};
const headerLabels: HeaderLabels[] = ["Name", "Description", "Count", "Status", "Has Product"];

const formatHeaderTestSelector = (header: HeaderLabels) => `bulkList_header_${header.replace(/\s+/g, "")}`;

const handleSorted = (header: string, sortedBy?: string) => {
    if (!sortedBy) {
        return false;
    }
    if (sortedBy?.includes(header)) {
        return sortedBy.includes("desc") ? "descending" : "ascending";
    }
    return "other";
};
type ProductStatus = "hasProduct" | "doesNotHaveProduct" | "indeterminate";
const getProductStatus = (hasProduct: boolean, selectorProductId: string | undefined): ProductStatus => {
    if (!selectorProductId) {
        return "indeterminate";
    }
    return hasProduct ? "hasProduct" : "doesNotHaveProduct";
};
interface BulkListTableProps extends WidgetProps {
    fields: {
        [fields.listType]: "wishList" | "buyingList";
    };
}
const BulkListTable: React.FC<BulkListTableProps> = ({ fields }) => {
    const areProductsLoading = useAreProductsLoading();
    const listType = fields.listType;
    const parentRef = React.useRef<HTMLDivElement>(null);
    const [showTopArrow, setShowTopArrow] = useState(false);
    const [sortedBy, setSortedBy] = useState<string>();
    const styles = dataTableStyles;
    const {
        lists,
        isLoading,
        selectedListIds: selectedItems,
        toggleAllItems,
        toggleItem,
        selectorProductId,
    } = useBulkLists(listType);
    const iconStyles = { ...styles.iconProps };
    if (!selectorProductId && styles.iconProps) {
        iconStyles.css = css`
            ${iconStyles.css}
            filter: grayscale(100%);
            opacity: 0.5;
        `;
    }

    const handleArrowVisibility = (
        showTopArrow: boolean,
        setShowTopArrow: React.Dispatch<boolean>,
        parentRef: React.RefObject<HTMLDivElement>,
        endIndex: number,
    ) => {
        if (!parentRef.current || !endIndex) {
            return;
        }

        const itemsPerPage = Math.floor(parentRef.current.clientHeight / 54);
        if (itemsPerPage < endIndex && !showTopArrow) {
            setShowTopArrow(true);
        }
        if (itemsPerPage >= endIndex && showTopArrow) {
            setShowTopArrow(false);
        }
    };

    const handleSortClick = useCallback(
        (header: string) => {
            if (sortedBy?.includes(header) && !sortedBy?.includes("desc")) {
                setSortedBy(`${header} desc`);
                return;
            }
            setSortedBy(header);
        },
        [sortedBy],
    );
    const overscan = 8;
    const elementHeight = 54;
    const sortedLists = useMemo(() => sortLists(lists, sortedBy), [lists, sortedBy]);
    const virtualizer = useVirtualizer({
        count: sortedLists.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => elementHeight,
        overscan,
    });
    handleArrowVisibility(showTopArrow, setShowTopArrow, parentRef, virtualizer.range?.endIndex || 0 - overscan);

    const handleIsHeaderChecked = useCallback(() => {
        if (!selectedItems.length) {
            return false;
        }
        if (selectedItems.length === lists?.length) {
            return true;
        }
        return "indeterminate";
    }, [lists, selectedItems]);

    const dataTableHeaders = useMemo(() => {
        const listIds = lists?.map(list => list.id);
        return [
            <DataTableHeader as="th" key="checkbox" {...styles.header} data-test-selector="bulkList_header_selectAll">
                <Checkbox
                    onChange={() => toggleAllItems(listIds || [])}
                    checked={handleIsHeaderChecked()}
                    data-test-selector="bulkList_selectAllCheckbox"
                />
            </DataTableHeader>,
            ...headerLabels.map(header => (
                <DataTableHeader
                    key={header}
                    data-test-selector={formatHeaderTestSelector(header)}
                    sorted={handleSorted(header, sortedBy)}
                    onSortClick={() => handleSortClick(header)}
                    {...styles.header}
                >
                    {header}
                </DataTableHeader>
            )),
        ];
    }, [handleIsHeaderChecked, handleSortClick, lists, sortedBy, styles.header, toggleAllItems]);
    const activeStyles = css`2px solid ${getColor("secondary")}`;

    return (
        <LoadingOverlay loading={isLoading || areProductsLoading}>
            <Modals />
            <StyledWrapper {...styles.parentContainer} ref={parentRef} data-test-selector="bulkList_container">
                <StyledWrapper
                    style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative" }}
                    data-test-selector="bulkList_tableSpacer"
                >
                    <DataTable {...styles.dataTable} data-test-selector="bulkList_table">
                        {customColGroup}
                        <DataTableHead {...styles.header}>{dataTableHeaders}</DataTableHead>
                        <DataTableBody>
                            {virtualizer.getVirtualItems().map((virtualRow, index) => {
                                const list = sortedLists[virtualRow.index];
                                const productStatus = getProductStatus(!!list?.hasProduct, selectorProductId);

                                const isActive = selectedItems.includes(list?.id);
                                if (!list) {
                                    return null;
                                }
                                return (
                                    <DataTableRow
                                        key={list?.id}
                                        style={{
                                            background:
                                                virtualRow.index % 2 === 0 ? "common.background" : "common.accent",
                                        }}
                                        id={virtualRow.index.toString()}
                                        css={css`
                                            height: ${virtualRow.size}px;
                                            transition: outline 0.4s ease-in-out;
                                            transform: translateY(${virtualRow.start - index * virtualRow.size}px);
                                            outline: ${isActive
                                                ? activeStyles
                                                : css`1px solid ${getColor("common.border")}`};
                                        `}
                                        data-test-selector={`bulkList_row_${list.id}`}
                                    >
                                        <DataTableCell data-test-selector={`bulkList_rowCheckboxCell_${list.id}`}>
                                            <Checkbox
                                                onChange={() => toggleItem(list.id)}
                                                checked={selectedItems.includes(list.id)}
                                                data-test-selector={`bulkList_rowCheckbox_${list.id}`}
                                            />
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.cell}
                                            data-test-selector={`bulkList_nameCell_${list.id}`}
                                        >
                                            {listType === "wishList" && (
                                                <MyListsDetailsPageTypeLink
                                                    title={list.name}
                                                    wishListId={list.id}
                                                    testSelector={`${list.name}`}
                                                />
                                            )}
                                            {listType === "buyingList" && (
                                                <BuyingListDetailsLink name={list.name} id={list.id} />
                                            )}
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.cell}
                                            title={list.description}
                                            data-test-selector={`bulkList_descriptionCell_${list.id}`}
                                        >
                                            {list.description}
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.cell}
                                            data-test-selector={`bulkList_countCell_${list.id}`}
                                        >
                                            {list.properties.wishListLineCount}
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.cell}
                                            title={getStatusText(list)}
                                            data-test-selector={`bulkList_statusCell_${list.id}`}
                                        >
                                            <WishListSharingStatus
                                                extendedStyles={styles.statusExtendedStyles}
                                                wishList={list}
                                                removeTooltip
                                            />
                                        </DataTableCell>
                                        <DataTableCell data-test-selector={`bulkList_hasProductCell_${list.id}`}>
                                            <Icon
                                                src={iconDictionary[productStatus]}
                                                {...iconStyles}
                                                data-test-selector={`bulkList_${productStatus}_Icon_${list.id}`}
                                            />
                                        </DataTableCell>
                                    </DataTableRow>
                                );
                            })}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
                <StyledWrapper
                    {...styles.scrollToTopArrow}
                    style={{ opacity: showTopArrow ? 1 : 0 }}
                    data-test-selector="bulkList_scrollToTopWrapper"
                >
                    <Icon
                        size={34}
                        onClick={() => virtualizer.scrollToIndex(0)}
                        aria-label="Scroll to top"
                        src={ArrowiOSUpward}
                        data-test-selector="bulkList_scrollToTop"
                    />
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: BulkListTable,
    definition: {
        group: "Common",
        displayName: "Bulk List Table",
        fieldDefinitions: [
            {
                name: "ListType",
                editorTemplate: "DropDownField",
                defaultValue: "wishList",
                fieldType: "General",
                options: [
                    { displayName: "My Lists", value: "wishList" },
                    { displayName: "Buying Lists", value: "buyingList" },
                ],
            },
        ],
    },
};

export default widgetModule;
